import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { formatAuditTimestamp } from "../../../utils/fmtDate";
import { maskAccount } from "../../../utils/bankMask";
import { verificationLabel } from "../../../utils/orderAccountKind";
import { bankCountryLabel, useCountries } from "../../../utils/bankDisplay";

/**
 * A version's bank country (Codex admin r5 P3). A7 makes the country part of
 * the account's identity, so a country-only change makes a new version whose
 * other coordinates are the same; without the country the two read alike.
 * NULL is a Canadian account by convention (bankCountryLabel). A server that
 * does not send the field at all gets "-", never a guessed Canada.
 */
const versionCountry = (countries, version) =>
  version && Object.prototype.hasOwnProperty.call(version, "bankCountryId")
    ? bankCountryLabel(countries, version.bankCountryId)
    : "-";

/**
 * A user's bank accounts, one card per investor account (corporate Business
 * PAD contract, 2026-10-25).
 *
 * Replaces the two blocks Viewuser rendered for every user from
 * user_banking_details[0] ("Bank Information (Individual)" and "(Corporate)",
 * full numbers included). The source is GET
 * admin/users/:userId/investor-payment-accounts: per investor account its kind,
 * its legal entity (ENTITY) and every instrument version. A coordinate change
 * creates a new version and supersedes the old one (I2), so the active version
 * is shown and the others are kept collapsed as the history. Accounts are
 * masked to the last 4 (I9) and nothing outside the contract's fields is
 * rendered. The proof document is private: it is streamed by GET
 * admin/investor-payment-accounts/:versionId/proof with the admin's token and
 * opened from an object URL, never from a public URL.
 *
 * Amendment A1: the read answers { userId, legalEntityName, accounts }, so the
 * decisions are bound to the answer itself (a non-AD reviewer cannot read
 * another user's profile through get-user-details-byid). They are enabled
 * only when the answer's userId is the user the cards were asked for. An
 * answer that names no user (a bare array, an older shape) is shown
 * read-only; one that names another user is not shown at all.
 *
 * Compliance verifies a company bank account before the first debit (I4).
 * Verify / Reject post { decision, note } to
 * admin/investor-payment-accounts/:id/verify; a note is required to reject.
 * The buttons render only when the caller passes canDecide (Amendment A1:
 * Viewuser and the compliance page pass the session's compliance seat, DR,
 * CCO or UDP; the legacy Super Admin wildcard never confers it; absent, it is
 * false) and only on the active version. The server enforces both
 * (requireComplianceRole('DR'), 409 on a superseded or inactive version), and
 * refuses VERIFIED of a company version with no proof (409
 * BANK_PROOF_REQUIRED); each 409 is described by its code (conflictText).
 *
 * The cards belong to the user the route names now: when it changes, the
 * previous user's cards go at once (never a decision on their instrument from
 * the next user's page), a late answer for another user is ignored, and the
 * decisions stay disabled while the accounts are being read. On Viewuser
 * (requireProfile) they also stay disabled unless the profile the page shows
 * (profileUserId, from Viewuser's profile read) is that same user: a decision
 * is never taken next to another user's identity, nor with no identity shown
 * at all. The compliance page shows no profile and does not ask for one.
 */

// Neutral on purpose: next to a Pending review account, "Verified by ..."
// would read as a status claim. Same wording as Viewuser's KYC decisions.
const OVERSIGHT_HINT = "Decided by a compliance reviewer";

const ACCOUNT_TYPES = {
  ENTITY: { CHECKING: "Business chequing", SAVINGS: "Business savings" },
  INDIVIDUAL: { CHECKING: "Chequing", SAVINGS: "Savings" },
};

const dash = (v) => (v === null || v === undefined || v === "" ? "-" : v);
const when = (v) => (v ? formatAuditTimestamp(v) : "-");

function accountTypeLabel(kind, type) {
  return (ACCOUNT_TYPES[kind] && ACCOUNT_TYPES[kind][type]) || dash(type);
}

// is_active is a TINYINT: a raw pass-through arrives as 1 or "1", not true.
const ACTIVE_VALUES = new Set([true, 1, "1"]);
const isActiveVersion = (v) => !!v && ACTIVE_VALUES.has(v.isActive) && !v.supersededAt;

/** The response body as a list: { data: [...] } or a bare array. */
function listOf(body) {
  if (Array.isArray(body)) return body;
  if (body && Array.isArray(body.data)) return body.data;
  return [];
}

const hasAccounts = (o) => !!o && !Array.isArray(o) && Array.isArray(o.accounts);

/**
 * The answer, bound to the user it names (Amendment A1):
 * { accounts, binding, legalEntityName }. binding is 'BOUND' when the
 * answer's userId is this user (ids compared as text: the route's is a
 * string, the API's a number), 'UNNAMED' when it names no user (a bare array,
 * the older { data: [...] }, a missing userId: read-only), 'FOREIGN' when it
 * names another user (none of its accounts are kept). The A1 object is read
 * bare or inside the API's { data } envelope.
 */
function bindAnswer(body, userId) {
  const a1 = hasAccounts(body) ? body : body && hasAccounts(body.data) ? body.data : null;
  if (!a1) return { accounts: listOf(body), binding: "UNNAMED", legalEntityName: null };
  const named = a1.userId !== null && a1.userId !== undefined && String(a1.userId).trim() !== "";
  if (!named) return { accounts: a1.accounts, binding: "UNNAMED", legalEntityName: null };
  if (String(a1.userId) !== String(userId)) return { accounts: [], binding: "FOREIGN", legalEntityName: null };
  return { accounts: a1.accounts, binding: "BOUND", legalEntityName: a1.legalEntityName || null };
}

function authHeaders() {
  return { "x-access-token": localStorage.getItem("accessToken") };
}

// A proof is opened in the console's own origin (an object URL), so only a
// type the browser shows without running it is opened: anything else (HTML,
// SVG, an unknown type) is saved as a file instead.
const VIEWABLE_TYPES = new Set(["application/pdf", "image/png", "image/jpeg", "image/gif", "image/webp"]);
// The tab loads the object URL after it is handed over: revoked later, not at once.
const REVOKE_AFTER_MS = 60 * 1000;

function proofFailureText(err) {
  // responseType blob: the error body is a Blob with no code, so key on status.
  const status = err?.response?.status;
  if (status === 404) return "No proof document on file.";
  if (status === 403) return "The proof document is not visible to your role.";
  return "The proof document could not be opened. Try again.";
}

function saveFile(url, name) {
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", name);
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * Open a version's proof: GET admin/investor-payment-accounts/:id/proof with
 * the admin's token, as a blob, shown in a new tab from an object URL. The tab
 * is opened in the click itself (one opened after the await is popup-blocked)
 * and closed again when nothing can be shown in it. -> null, or the reason
 * the proof was not opened.
 */
async function openProof(version) {
  const tab = window.open("", "_blank");
  try {
    const res = await axios.get(
      `${process.env.REACT_APP_API}admin/investor-payment-accounts/${version.id}/proof`,
      { headers: authHeaders(), responseType: "blob" }
    );
    const served = String((res?.headers && res.headers["content-type"]) || (res?.data && res.data.type) || "")
      .split(";")[0]
      .trim()
      .toLowerCase();
    const viewable = VIEWABLE_TYPES.has(served);
    const blob = new Blob([res.data], { type: viewable ? served : "application/octet-stream" });
    const url = window.URL.createObjectURL(blob);
    if (viewable && tab) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      if (tab) tab.close();
      saveFile(url, (version.proofDocument && version.proofDocument.title) || `bank-proof-${version.id}`);
    }
    setTimeout(() => window.URL.revokeObjectURL(url), REVOKE_AFTER_MS);
    return null;
  } catch (err) {
    if (tab) tab.close();
    return proofFailureText(err);
  }
}

const ProofButton = ({ version }) => {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);
  const open = async () => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    const failure = await openProof(version);
    if (!mounted.current) return;
    setNotice(failure);
    setBusy(false);
  };
  return (
    <>
      <button type="button" className="approveButton downloadbtn" onClick={open} disabled={busy}>
        Open proof ({version.proofDocument.title || "document"})
      </button>
      {notice && <span className="muted"> {notice}</span>}
    </>
  );
};

const Item = ({ label, children }) => (
  <div className="detailItem">
    <span className="itemKey">{label}:</span>
    <span className="itemValue">{children}</span>
  </div>
);

/**
 * A 409 from POST .../:id/verify, read by its code (review round 3,
 * 2026-10-25: every 409 used to read as superseded or inactive). The code is
 * `code`, or `error` as the role gate sends it.
 *   - BANK_PROOF_REQUIRED: VERIFIED of a company version with no proof
 *     document on file (contract, Admin API).
 *   - a code naming a superseded or inactive version: SUPERSEDED, INACTIVE or
 *     NOT_ACTIVE, bare or with a VERSION_ or INSTRUMENT_ prefix (the contract
 *     names no code for that refusal, only the 409).
 *   - anything else, or no code at all: the server refused the decision, and
 *     its code is named (never guessed as superseded).
 * The accounts reload after every 409 either way.
 */
// Only a code naming the version (or its instrument) itself: ACCOUNT_INACTIVE
// is a user account's liveness in the API's vocabulary, and PAD_SUPERSEDED is
// a PAD, so they are unknown codes here, never read as a superseded version.
const SUPERSEDED_CODE = /^(VERSION_|INSTRUMENT_)?(SUPERSEDED|INACTIVE|NOT_ACTIVE)$/;

function conflictText(data) {
  const raw = data && (data.code || data.error);
  const code = typeof raw === "string" ? raw.trim().toUpperCase() : "";
  if (code === "BANK_PROOF_REQUIRED") {
    return "Refused: a company account is verified only against a void cheque or bank letter showing the company as the account holder, and none is on file for this version. It must be on file before this account can be verified. The accounts were reloaded.";
  }
  if (code && SUPERSEDED_CODE.test(code)) {
    return "Refused: this version is no longer the active one (it was superseded or deactivated). The accounts were reloaded: decide on the current version.";
  }
  if (code) return `Refused: the server refused this decision (code ${code}). The accounts were reloaded.`;
  return "Refused: the server refused this decision and gave no code. The accounts were reloaded.";
}

function refusalText(err) {
  const status = err?.response?.status;
  const data = err?.response?.data || {};
  if (status === 403) {
    return "Only a Dealing Representative, CCO or UDP can verify or reject a bank account.";
  }
  if (status === 409) return conflictText(data);
  if (status === 400) {
    return typeof data.message === "string" && data.message.trim()
      ? `Refused: ${data.message}`
      : "Refused: a note is required to reject.";
  }
  return "The decision could not be recorded. Reload the page to see whether it was saved.";
}

// A company account is verified against its void cheque or bank letter, so
// with no proof document on file only Reject is offered (the server decides
// either way).
const PROOF_REQUIRED_HINT = "A company account can be verified only against a void cheque or bank letter on file.";
const KIND_UNKNOWN_HINT =
  "The kind of this account is not known (company or individual), so it cannot be verified here. It can still be rejected.";

/**
 * Why Verify is not offered on this version, or null. Only an account whose
 * kind is exactly INDIVIDUAL is verified without a proof; an ENTITY account
 * needs its proof document on file (proofDocument.id); an account of any
 * other kind (null, absent, unknown) cannot be verified here (fail closed).
 * Reject is never blocked by this.
 */
function verifyBlock(kind, version) {
  if (kind === "INDIVIDUAL") return null;
  if (kind === "ENTITY") return version.proofDocument && version.proofDocument.id ? null : PROOF_REQUIRED_HINT;
  return KIND_UNKNOWN_HINT;
}

const DecisionControls = ({ kind, version, onResult, locked }) => {
  const [mode, setMode] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // A decision whose answer lands after the cards are gone (another user's
  // page, or the page left) is dropped unread: nothing is set or reloaded
  // (review round 3, 2026-10-25).
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
  }, []);
  const noteMissing = mode === "REJECTED" && !note.trim();
  const verifyBlocked = verifyBlock(kind, version);

  const open = (decision) => {
    if (decision === "VERIFIED" && verifyBlocked) return;
    setMode(decision);
    setNote("");
  };

  const submit = async () => {
    if (!mode || noteMissing || busy || locked) return;
    if (mode === "VERIFIED" && verifyBlocked) return;
    const trimmed = note.trim();
    const body = trimmed ? { decision: mode, note: trimmed } : { decision: mode };
    setBusy(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API}admin/investor-payment-accounts/${version.id}/verify`,
        body,
        { headers: authHeaders() }
      );
      if (!mounted.current) return;
      onResult({ tone: "ok", text: `Recorded: ${verificationLabel(mode)}.` }, true);
      setMode(null);
    } catch (err) {
      if (!mounted.current) return;
      onResult({ tone: "danger", text: refusalText(err) }, err?.response?.status === 409);
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  return (
    <div className="bank-decision" style={{ marginTop: 8 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          className="approveButton"
          onClick={() => open("VERIFIED")}
          disabled={busy || locked || !!verifyBlocked}
        >
          Verify
        </button>
        <button type="button" className="approveButton" onClick={() => open("REJECTED")} disabled={busy || locked}>
          Reject
        </button>
      </div>
      {verifyBlocked && <p className="muted">{verifyBlocked}</p>}
      {mode && (
        <div style={{ marginTop: 8 }}>
          <label htmlFor={`bank-note-${version.id}`} style={{ display: "block", fontSize: 13 }}>
            {mode === "REJECTED" ? "Note (required to reject)" : "Note (optional)"}
          </label>
          <textarea
            id={`bank-note-${version.id}`}
            rows={3}
            maxLength={500}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={busy}
            style={{ width: "100%" }}
          />
          {noteMissing && <p className="muted">A note is required to reject.</p>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="approveButton" onClick={submit} disabled={busy || noteMissing || locked}>
              Record decision
            </button>
            <button type="button" className="approveButton" onClick={() => setMode(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const VersionDetails = ({ kind, version, countries }) => (
  <div className="user-que-info">
    <Item label="Account holder">{dash(version.holderName)}</Item>
    <Item label="Bank country">{versionCountry(countries, version)}</Item>
    <Item label="Bank name">{dash(version.bankName)}</Item>
    <Item label="Institution number">{dash(version.institutionNumber)}</Item>
    <Item label="Transit number">{dash(version.transitNumber)}</Item>
    <Item label="Account number">{maskAccount(version.accountLast4) || "-"}</Item>
    <Item label="Account type">{accountTypeLabel(kind, version.accountType)}</Item>
    <Item label="Verification">
      <span className="bank-status" data-status={version.verificationStatus}>
        {verificationLabel(version.verificationStatus)}
      </span>
      {version.verificationNote ? ` (${version.verificationNote})` : ""}
    </Item>
    <Item label="Decided by">
      {version.verifiedBy ? `#${version.verifiedBy}, ${when(version.verifiedAt)}` : "-"}
    </Item>
    {kind === "ENTITY" && (
      <Item label="Signing authority attested">{when(version.authorityAttestedAt)}</Item>
    )}
    <Item label="Proof">
      {version.proofDocument && version.proofDocument.id ? (
        <ProofButton version={version} />
      ) : (
        "No proof on file"
      )}
    </Item>
    <Item label="Version">#{version.id}, created {when(version.createdAt)}</Item>
  </div>
);

const AccountCard = ({ account, canDecide, locked, onReload, countries }) => {
  const [result, setResult] = useState(null);
  const versions = Array.isArray(account.versions) ? account.versions : [];
  const active = versions.find(isActiveVersion);
  const kind = account.kind;
  const title =
    kind === "ENTITY"
      ? `Company account: ${account.legalEntity?.name || "no legal entity linked"}`
      : kind === "INDIVIDUAL"
      ? "Individual account"
      : "Account of unknown kind";

  const handleResult = (message, reload) => {
    setResult(message);
    if (reload) onReload();
  };

  return (
    <div className="bank-account-card" data-account-kind={kind} style={{ marginBottom: 16 }}>
      <h5 style={{ fontSize: 15, margin: "8px 0" }}>
        {title} <span className="muted">(investor account #{account.investorAccountId})</span>
      </h5>
      {active ? (
        <div data-testid="active-version">
          <VersionDetails kind={kind} version={active} countries={countries} />
          {canDecide ? (
            <DecisionControls
              key={active.id}
              kind={kind}
              version={active}
              onResult={handleResult}
              locked={locked}
            />
          ) : (
            <p className="muted">{OVERSIGHT_HINT}</p>
          )}
        </div>
      ) : (
        <p className="muted">No active bank account version.</p>
      )}
      {result && (
        <p role="status" style={{ color: result.tone === "danger" ? "#b42318" : "#067647" }}>
          {result.text}
        </p>
      )}
      {versions.length > 0 && (
        <details>
          <summary>Version history ({versions.length})</summary>
          <ul style={{ margin: "8px 0", paddingLeft: 18 }}>
            {versions.map((v) => (
              <li key={v.id}>
                #{v.id}, created {when(v.createdAt)}: {dash(v.bankName)}{" "}
                {maskAccount(v.accountLast4) || "-"}, {versionCountry(countries, v)},{" "}
                {verificationLabel(v.verificationStatus)}
                {isActiveVersion(v) ? ", active" : `, Superseded ${when(v.supersededAt)}`}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

const UNNAMED_ANSWER =
  "Verify and Reject are off: the server's answer does not name the user these accounts belong to.";
const FOREIGN_ANSWER =
  "The server answered with another user's accounts, so none are shown. Reload the page.";

// Shown when the caller does not let this session decide (an AD alone, any
// non-compliance role): who does.
const READ_ONLY =
  "Read only: a compliance reviewer (a Dealing Representative, CCO or UDP) verifies or rejects these accounts.";

const PROFILE_HOLD =
  "Verify and Reject need this user's profile on this page, and it is not shown (it is still loading, or it is not visible to your role).";

const InvestorPaymentAccounts = ({ userId, canDecide: decideProp = false, requireProfile = false, profileUserId }) => {
  // Only an explicit true lets this session decide (fail closed).
  const canDecide = decideProp === true;
  const countries = useCountries();
  // With requireProfile (Viewuser), the profile shown next to the cards must
  // be this user's (ids compared as text: the route's is a string, the
  // profile's a number). No profile: no decision.
  const profileShown =
    !requireProfile ||
    (profileUserId !== undefined && profileUserId !== null && String(profileUserId) === String(userId));
  // What was read, and for which user: shown only while the route names that user.
  const [loaded, setLoaded] = useState({
    userId: null,
    state: "loading",
    accounts: [],
    binding: "UNNAMED",
    legalEntityName: null,
  });
  const [fetching, setFetching] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!userId) return undefined;
    let live = true;
    setFetching(true);
    axios
      .get(`${process.env.REACT_APP_API}admin/users/${userId}/investor-payment-accounts`, {
        headers: authHeaders(),
      })
      .then((res) => {
        if (!live) return;
        setLoaded({ userId, state: "ready", ...bindAnswer(res?.data, userId) });
      })
      .catch((err) => {
        if (!live) return;
        setLoaded({
          userId,
          state: err?.response?.status === 403 ? "refused" : "failed",
          accounts: [],
          binding: "UNNAMED",
          legalEntityName: null,
        });
      })
      .finally(() => {
        if (live) setFetching(false);
      });
    return () => {
      live = false;
    };
  }, [userId, reloadKey]);

  const current = loaded.userId === userId;
  const state = current ? loaded.state : "loading";
  const accounts = current ? loaded.accounts : [];
  // A1: decisions only on an answer that names this user.
  const bound = current && state === "ready" && loaded.binding === "BOUND";
  const unnamed = current && state === "ready" && loaded.binding === "UNNAMED" && accounts.length > 0;

  let body;
  if (state === "refused") body = <p className="muted">Bank accounts are not visible to your role.</p>;
  else if (state === "failed") body = <p className="muted">Bank accounts could not be loaded.</p>;
  else if (state === "loading") body = <p className="muted">Loading bank accounts.</p>;
  else if (loaded.binding === "FOREIGN") body = <p className="muted">{FOREIGN_ANSWER}</p>;
  else if (accounts.length === 0) body = <p className="muted">No bank account is on file for this user.</p>;
  else {
    body = accounts.map((account) => (
      <AccountCard
        key={account.investorAccountId}
        account={account}
        canDecide={canDecide}
        locked={fetching || !bound || !profileShown}
        onReload={() => setReloadKey((k) => k + 1)}
        countries={countries}
      />
    ));
  }

  return (
    <div className="user-que-main-div investor-payment-accounts">
      <h4 className="user-que-subtitle">Bank accounts</h4>
      {bound && (
        <p className="muted">
          {`Accounts of user #${userId}.`}
          {loaded.legalEntityName ? ` Legal entity: ${loaded.legalEntityName}` : ""}
        </p>
      )}
      {!canDecide && accounts.length > 0 && <p className="muted">{READ_ONLY}</p>}
      {canDecide && unnamed && <p className="muted">{UNNAMED_ANSWER}</p>}
      {canDecide && !profileShown && accounts.length > 0 && <p className="muted">{PROFILE_HOLD}</p>}
      {body}
    </div>
  );
};

export default InvestorPaymentAccounts;
